import { useEffect } from 'react';
import { Link, Route, Routes, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { SyncIndicator } from './components/SyncIndicator';
import { ProtectedRoute } from './components/ProtectedRoute';
import { useAuth, useCanManageAnyLocation } from './store/auth';
import * as outbox from './db/outbox';
import { ensurePushSubscription } from './data/push';
import { PASSWORD_CHANGE_REQUIRED_EVENT, UNAUTHORIZED_EVENT } from './lib/api';
import { captureAppVersionFromUrl } from './lib/platform';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import SetInitialPasswordPage from './pages/SetInitialPasswordPage';
import HomePage from './pages/HomePage';
import DashboardPage from './pages/DashboardPage';
import ShiftsPage from './pages/ShiftsPage';
import CalendarPage from './pages/CalendarPage';
import OverviewPage from './pages/OverviewPage';
import UsersAdminPage from './pages/UsersAdminPage';
import LocationsAdminPage from './pages/LocationsAdminPage';
import LocationDetailPage from './pages/LocationDetailPage';
import SettingsAdminPage from './pages/SettingsAdminPage';
import WeeklyPlanPage from './pages/WeeklyPlanPage';
import TeamOverviewPage from './pages/TeamOverviewPage';
import CompanyAdminPage from './pages/CompanyAdminPage';
import DownloadPage from './pages/DownloadPage';
import ImpressumPage from './pages/ImpressumPage';
import DatenschutzPage from './pages/DatenschutzPage';
import AgbPage from './pages/AgbPage';
import ClockOutPage from './pages/ClockOutPage';
import { ClockStatusBar } from './components/ClockStatusBar';
import { AdminChefViewBanner, CompanySwitcher } from './components/CompanySwitcher';
import { OnboardingTutorial } from './components/OnboardingTutorial';
import { RecoveryCodeModal, WageKeyBanner } from './components/WageKey';

// Bei jedem App-Start: TWA-Version aus URL einlesen und persistieren.
// Wird VOR der ersten React-Router-Navigation gerufen, weil React-Router
// die Query-Params bei einigen Setups nach dem ersten Render rauswerfen kann.
captureAppVersionFromUrl();

export default function App() {
  const hydrate = useAuth((s) => s.hydrate);
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const refreshMe = useAuth((s) => s.refreshMe);
  const setActiveCompany = useAuth((s) => s.setActiveCompany);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Kapitel 3.2 (Screen 7): bis ein eigenes Passwort festgelegt ist, nur die
  // Seite „Passwort festlegen" — keine Stempel-Leiste, kein Tutorial, kein
  // Firmen-Umschalter/Sync (die würden sonst API-Aufrufe starten, die der
  // Server in diesem Zustand mit 403 ablehnt).
  const appUser = user && !user.passwordIsInitial ? user : null;

  // Beim App-Start: laufenden Token validieren / User aktualisieren.
  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  // Shadow-Report 19.09.2026: Das eigene Konto (z. B. die Freigabe „Tagesgeld
  // entnehmen“ oder ein geänderter Lohn) wurde nur beim App-Start geladen —
  // auf einem dauerhaft offenen Tablet kam eine Änderung des Chefs nie an.
  // Jetzt zusätzlich alle 10 Minuten und beim Zurückwechseln zur App.
  useEffect(() => {
    if (!user) return;
    let last = Date.now();
    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - last < 60_000) return; // nicht bei jedem Tab-Wechsel
      last = Date.now();
      void refreshMe().catch(() => undefined);
    };
    const id = setInterval(refresh, 10 * 60_000);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [user, refreshMe]);

  // Server meldet „erst eigenes Passwort festlegen" (z. B. weil Chef/
  // Filialleitung es gerade neu gesetzt hat) → User neu laden; ProtectedRoute
  // leitet dann auf /passwort-festlegen um.
  useEffect(() => {
    function onPasswordChangeRequired() {
      void refreshMe().catch(() => undefined);
    }
    window.addEventListener(PASSWORD_CHANGE_REQUIRED_EVENT, onPasswordChangeRequired);
    return () => window.removeEventListener(PASSWORD_CHANGE_REQUIRED_EVENT, onPasswordChangeRequired);
  }, [refreshMe]);

  // Service-Worker schickt uns `{ kind: 'navigate', url }` wenn der User in
  // einer Notification den "Ausstempeln"-Action-Button drückt — wir routen
  // dann clientseitig zur Ziel-URL.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
    function onMessage(ev: MessageEvent) {
      const data = ev.data as { kind?: string; url?: string } | undefined;
      if (data?.kind === 'navigate' && typeof data.url === 'string') {
        navigate(data.url);
      }
    }
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => navigator.serviceWorker.removeEventListener('message', onMessage);
  }, [navigate]);

  // Gesamtprüfung 11.09.2026: Push-Anmeldung dieses Geräts nach dem Login
  // der angemeldeten Person zuordnen (geteiltes Gerät / erneuter Login).
  const token = useAuth((s) => s.token);
  const appUserId = appUser?.id;
  useEffect(() => {
    if (token && appUserId) void ensurePushSubscription(token, appUserId);
  }, [token, appUserId]);

  // Gesamtprüfung 11.09.2026: Sitzung serverseitig ungültig (401) → sauber
  // abmelden. Noch nicht gesendete Änderungen bleiben dabei erhalten (sie
  // sind an die Person gebunden und werden nach dem nächsten Login gesendet).
  useEffect(() => {
    function onUnauthorized() {
      if (!useAuth.getState().token) return;
      logout({ forced: true });
      navigate('/login', { replace: true });
    }
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [logout, navigate]);

  async function onLogout() {
    // Gesamtprüfung 11.09.2026: vor dem Abmelden warnen, wenn noch Änderungen
    // offline warten — beim Abmelden werden sie (und alle lokalen Daten)
    // gelöscht. Das eigentliche Aufräumen macht useAuth.logout().
    const pending = await outbox.pendingCount().catch(() => 0);
    if (
      pending > 0 &&
      !confirm(
        `${pending} Änderung${pending === 1 ? '' : 'en'} ${pending === 1 ? 'ist' : 'sind'} noch nicht synchronisiert und ${pending === 1 ? 'geht' : 'gehen'} beim Abmelden verloren. Trotzdem abmelden?`,
      )
    ) {
      return;
    }
    logout();
    navigate('/login', { replace: true });
  }

  return (
    <div className="min-h-screen flex flex-col">
      {appUser && <ClockStatusBar />}
      {appUser && <OnboardingTutorial />}
      <header className="bg-surface border-b border-border">
        <div className="mx-auto w-full max-w-7xl px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-2">
          <Link to="/" className="text-sm sm:text-lg font-semibold !min-h-0 shrink-0 inline-flex items-center gap-1.5">
            <img src="/icon.svg" alt="" className="w-6 h-6 rounded" aria-hidden />
            ShiftSync
          </Link>
          <div className="flex items-center gap-1 sm:gap-2 justify-end flex-wrap">
            {appUser && <CompanySwitcher />}
            {appUser && <SyncIndicator />}
            {user && (
              <>
                <Link
                  to="/profil"
                  className="text-[11px] sm:text-xs px-2 sm:px-3 py-1 rounded bg-slate-700 hover:bg-slate-600 !min-h-0"
                  aria-label="Profil"
                >
                  Profil
                </Link>
                <button
                  onClick={onLogout}
                  className="text-[11px] sm:text-xs px-2 sm:px-3 py-1 rounded bg-slate-700 hover:bg-slate-600 !min-h-0"
                  aria-label="Abmelden"
                >
                  Abmelden
                </button>
              </>
            )}
          </div>
        </div>
      </header>
      {/* Phase L (Kapitel 7.1): Lohn-Schlüssel gesperrt/fehlt + Einmal-Anzeige des Wiederherstellungs-Codes */}
      {appUser && <WageKeyBanner />}
      {user && <RecoveryCodeModal />}
      {appUser && (
        <AdminChefViewBanner
          onExit={() => {
            setActiveCompany(null);
            void queryClient.resetQueries();
            navigate('/');
          }}
        />
      )}

      <main className="flex-1 px-3 sm:px-6 py-4 sm:py-6">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/passwort-vergessen" element={<ForgotPasswordPage />} />
          <Route path="/passwort-zuruecksetzen" element={<ResetPasswordPage />} />
          <Route path="/passwort-festlegen" element={<SetInitialPasswordPage />} />
          <Route path="/download" element={<DownloadPage />} />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/datenschutz" element={<DatenschutzPage />} />
          <Route path="/agb" element={<AgbPage />} />
          <Route
            path="/clockout/:id"
            element={
              <ProtectedRoute>
                <ClockOutPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <StartPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/meine-ansicht"
            element={
              <ProtectedRoute>
                <ShiftsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kalender"
            element={
              <ProtectedRoute>
                <CalendarPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/uebersicht"
            element={
              <ProtectedRoute>
                <OverviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profil"
            element={
              <ProtectedRoute>
                <HomePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/wochenplan"
            element={
              <ProtectedRoute requireManageAnyLocation>
                <WeeklyPlanPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team"
            element={
              <ProtectedRoute requireManage>
                <TeamOverviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute requireManageAnyLocation>
                <UsersAdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/locations"
            element={
              <ProtectedRoute requireManageAnyLocation>
                <LocationsAdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/locations/:id"
            element={
              <ProtectedRoute requireManageAnyLocation>
                <LocationDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/companies"
            element={
              <ProtectedRoute requireRole={['admin', 'superadmin']}>
                <CompanyAdminPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/einstellungen"
            element={
              // Phase P (Kapitel 20.5, Entscheidung #26): minijobLimitEur ist seit
              // der Firmen-Umstellung Chef-editierbar (requireCompanyOwner im
              // Backend), nicht mehr global-Admin-exklusiv — Route entsprechend
              // nachgezogen (war ein übersehener Rest aus der Phase-P-Umstellung).
              <ProtectedRoute requireManage>
                <SettingsAdminPage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      <footer className="text-center text-xs text-slate-500 py-3 border-t border-border space-y-1">
        <div className="flex flex-wrap justify-center gap-x-3 gap-y-1">
          <Link
            to="/impressum"
            className="text-slate-400 hover:text-slate-200 !min-h-0 underline"
          >
            Impressum
          </Link>
          <Link
            to="/datenschutz"
            className="text-slate-400 hover:text-slate-200 !min-h-0 underline"
          >
            Datenschutz
          </Link>
          <Link
            to="/agb"
            className="text-slate-400 hover:text-slate-200 !min-h-0 underline"
          >
            AGB
          </Link>
          <Link
            to="/download"
            className="text-slate-400 hover:text-slate-200 !min-h-0 underline"
          >
            App-Download
          </Link>
        </div>
        <div>v1.1.0 · {new Date().getFullYear()} · DevHive Network</div>
      </footer>
    </div>
  );
}

/**
 * Startseite nach dem Login (Route "/") — rollenabhängig, siehe Design-Canvas
 * "ShiftSync Rollenansichten": Chef/Filialleitung bekommen das neue Dashboard
 * (DashboardPage), reine Mitarbeiter landen unverändert direkt auf der
 * eigenen Schicht-Liste (ShiftsPage) — kein zusätzlicher Klick für die
 * größte Nutzergruppe. "Meine Ansicht" auf dem Dashboard führt auf
 * /meine-ansicht, dieselbe ShiftsPage-Komponente unter anderer Route.
 */
function StartPage() {
  const canManageAnyLocation = useCanManageAnyLocation();
  return canManageAnyLocation ? <DashboardPage /> : <ShiftsPage />;
}

function NotFound() {
  return (
    <div className="text-center text-slate-400 mt-8">
      <p className="text-3xl mb-2">404</p>
      <p>Seite nicht gefunden.</p>
    </div>
  );
}
